import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTutorial } from "./TutorialProvider";

export function PageTutorialDialog() {
  const navigate = useNavigate();
  const {
    content,
    open,
    dismiss,
    showNextLogin,
    setShowNextLogin,
  } = useTutorial();

  if (!content) return null;

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) dismiss();
  };

  const handleCta = () => {
    const href = content.cta?.href;
    dismiss();
    if (!href) return;
    const path = window.location.pathname.replace(/\/$/, "") || "/";
    if (href.startsWith("/settings") && path === "/settings") {
      document.getElementById("stores")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (href.startsWith("/")) {
      navigate(href);
    } else {
      window.location.href = href;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl pr-6">
            {content.pageName}
          </DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-400 text-base">
            {content.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {content.sections.map((section) => (
            <div key={section.heading}>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                {section.heading}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {section.body}
              </p>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-col items-stretch gap-3 sm:space-x-0">
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <Checkbox
              checked={showNextLogin}
              onCheckedChange={(checked) => setShowNextLogin(checked === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Show this next time you log in
            </span>
          </label>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            {content.cta && (
              <Button
                type="button"
                variant="outline"
                onClick={handleCta}
                className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {content.cta.label}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              onClick={dismiss}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Dismiss
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
